import { useCallback, useContext, useEffect, useState } from "react";
import BundleIcon from "../../common/icons/bundle.svg";
import ManualOfferIcon from "../../common/icons/manualOffer.svg";
import OrderCancelledIcon from "../../common/icons/orderCancelled.svg";

import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { format, isThisISOWeek, isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  FilterSearchField,
  SearchFilter,
} from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { Heading1 } from "../../common/components/styled/styled";
import { ItemGroup } from "../invoices/invoiceAPI";
import { OrderFilters } from "./filters/OrderFilters";
import { OrdersTable } from "./components/OrdersTable";
import { OrderRowDisplay, OrderType } from "./orderAPI";
import { fetchOrderRows, selectAllOrderRows } from "./orderRowsSlice";

import { NotificationsActiveOutlined as NotificationIcon } from "@mui/icons-material";
import CircleIcon from "@mui/icons-material/Circle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { OrderFulfillmentInfo } from "./components/OrderFulfillmentInfo";
import { useNavigate } from "react-router-dom";
import { HoverPopper } from "../../common/components/functional/hoverPopper/HoverPopper";
import { ORDERS_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../common/components/functional/pagination/types";
import getLocale from "../../common/helpers/getLocale";
import isEmpty from "../../common/helpers/isEmpty";
import { ReactComponent as ClockIcon } from "../../common/icons/clock.svg";
import { useTablePagination } from "../../hooks/useTablePagination";
import { AccessRightContext } from "../user/AccessRightProvider";
import { isExpertCustomerServiceOrAdmin } from "../user/accessRightChecks";
import { getAssignedTalents } from "./assignedTalentHelpers";
import { OrderRowPopperContent } from "./components/OrderRowPopperContent";
import { ORDER_FILTERS_LOCAL_STORAGE, ORDER_STATUS_EXPIRED } from "./constants";
import { OrderGroup, OrderRowDisplayFormatted } from "./types/common";
import { formatFlexibleStartTime } from "./utils";
import { mergeInitialAndStoredFilters } from "../../common/helpers/filters";
import { OrderQuickFilterButtons } from "./filters/OrderQuickFilterButtons";
import {
  removeQuickFilterFromLocalStorage,
  setFiltersToLocalStorage,
  setQuickFilterToLocalStorage,
} from "../../common/components/functional/FilterSearchField/helpers";
import {
  defaultOrderFilters,
  ORDER_QUICK_FILTER_LOCAL_STORAGE,
  OrderQuickFilterButtonType,
} from "./filters/common";

const pageSize = parseInt(ORDERS_PAGE_SIZE, 10);

const getQuickFilterFromLocalStorage = (): OrderQuickFilterButtonType | "" => {
  const value = localStorage.getItem(ORDER_QUICK_FILTER_LOCAL_STORAGE);
  return (value as OrderQuickFilterButtonType) || "";
};

export const OrdersPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["order", "common"]);
  const {
    localized,
    user: { user },
  } = useAppSelector((state) => state);

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");

  const navigate = useNavigate();

  const fromLocalStorage = localStorage.getItem(ORDER_FILTERS_LOCAL_STORAGE);
  const userFilters: SearchFilter[] =
    fromLocalStorage && JSON.parse(fromLocalStorage);

  // "statuses", "types" and "workLocations" have been renamed and changed from string to array so
  // need to account for old existing user filters
  // TODO delete later?
  const sanitizedUserFilters: SearchFilter[] = userFilters?.map((filter) => {
    if (filter.name === "fulfilled") {
      return { name: "statuses", value: [] };
    } else if (filter.name === "type") {
      return { name: "types", value: [filter.value] };
    } else if (filter.name === "workLocation") {
      return {
        name: "workLocations",
        value: [filter.value],
        displayValue: filter.displayValue,
      };
      // prevent FilterSearchField crashing if value is null or undefined
    } else if (filter.value === null || filter.value === undefined) {
      console.log(
        "Got null or undefined filter value from local storage for: ",
        filter.name,
      );
      return { ...filter, value: "" };
    } else {
      return filter;
    }
  });

  // Make sure new filters are added to user filters, but existing filter values are not overridden by default filters
  const combinedFilters: SearchFilter[] = mergeInitialAndStoredFilters(
    defaultOrderFilters,
    sanitizedUserFilters || [],
  );

  const [filters, setFilters] = useState<SearchFilter[]>(combinedFilters);

  const [selectedQuickFilter, setSelectedQuickFilter] = useState<
    OrderQuickFilterButtonType | ""
  >(getQuickFilterFromLocalStorage());

  const { canReadOrder } = useContext(AccessRightContext);
  const isCustomerServiceOrAdmin = isExpertCustomerServiceOrAdmin(user);

  const { status } = useAppSelector((state) => state.orderRows);
  const isLoading = status === "pending";
  const [orderGroups, setOrderGroups] = useState<
    ItemGroup<OrderRowDisplayFormatted>[]
  >([]);
  const orderRows = useAppSelector((state) => selectAllOrderRows(state));
  const { cursor, hits } = useAppSelector((state) => state.orderRows);

  const theme = useTheme();

  const dispatchSearch = useCallback(
    (cursor?: string | null) => {
      const filterObject = filters.reduce((acc, filter) => {
        const { name, value } = filter;
        return { ...acc, [name]: value };
      }, {});
      dispatch(
        fetchOrderRows({
          search: searchString,
          filters: filterObject,
          pageSize: pageSize.toString(),
          ...(cursor && { cursor }),
        }),
      );
    },
    [dispatch, filters, searchString],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<OrderRowDisplay>({
      data: orderRows,
      cursor,
      hits,
      pageSize,
      fetchNext: (cursor) => dispatchSearch(cursor),
    });

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize,
  };

  const handleFilterMenuOpenClose = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  const selectPrevTerm = (term: string) => {
    setSearchString(term);
  };

  const handleQuickFilterChange = useCallback(
    (quickFilter: OrderQuickFilterButtonType | "", filters: SearchFilter[]) => {
      setSelectedQuickFilter(quickFilter);
      setFilters(filters);
      setFiltersToLocalStorage(filters, ORDER_FILTERS_LOCAL_STORAGE);

      if (quickFilter) {
        setQuickFilterToLocalStorage(
          quickFilter,
          ORDER_QUICK_FILTER_LOCAL_STORAGE,
        );
      } else {
        removeQuickFilterFromLocalStorage(ORDER_QUICK_FILTER_LOCAL_STORAGE);
      }
    },
    [],
  );

  const updateFilters = (filters: any) => {
    setFilters(filters);
    setSelectedQuickFilter("");
    localStorage.setItem(ORDER_FILTERS_LOCAL_STORAGE, JSON.stringify(filters));
    removeQuickFilterFromLocalStorage(ORDER_QUICK_FILTER_LOCAL_STORAGE);
  };

  const getStatusIcons = (order: OrderRowDisplay) => {
    let icons = [];
    if (!isEmpty(order.teamNotificationSettings) && isCustomerServiceOrAdmin) {
      icons.push(
        <NotificationIcon
          key="notification"
          sx={{
            width: "18px",
            height: "18px",
            flexShrink: 0,
            marginRight: "5px",
          }}
        />,
      );
    }
    if (order.status === "Cancelled") {
      icons.push(
        <img
          src={OrderCancelledIcon}
          alt={"cancelled"}
          style={{ marginRight: "5px" }}
          key="cancelled"
        />,
      );
    }
    if (order.orderRowIds.length > 1)
      icons.push(
        <img
          src={BundleIcon}
          alt={"bundle"}
          style={{ marginRight: "5px" }}
          key="bundle"
        />,
      );
    if (order.manuallyOfferedTalents.length > 0)
      icons.push(
        <img
          src={ManualOfferIcon}
          alt={"manual offers"}
          style={{ marginRight: "5px" }}
          key="manualOffer"
        />,
      );
    if (order.isFillShift) {
      icons.push(
        <CircleIcon
          sx={{
            color: theme.palette.warning.light,
            height: "10px",
            width: "10px",
          }}
          key="isFillShift"
        />,
      );
    }
    return (
      <Box display="flex" alignItems="center">
        {icons}
      </Box>
    );
  };

  const formatTime = (order: OrderRowDisplay) => {
    if (order.orderRowIds.length > 1 || order.type !== OrderType.Short) {
      return null;
    }

    return `${order.workStarts ? format(new Date(order.workStarts), "HH:mm") : ""} - ${
      order.workEnds ? format(new Date(order.workEnds), "HH:mm") : ""
    }`;
  };

  const formatDate = (order: OrderRowDisplay, orderStartDate: Date) => {
    const startDate = new Date(orderStartDate);
    const endDate = order?.workEnds ? new Date(order.workEnds) : null;

    const formattedStart = format(startDate, "d.M");
    const formattedEnd = endDate ? format(endDate, "d.M") : "";

    if (order.type === OrderType.Short && order.orderRowIds.length === 1) {
      // Single shift orders
      const formattedStartDate = format(orderStartDate, "eeeeee d.M", {
        locale: getLocale(),
      });

      return `${formattedStartDate.charAt(0).toUpperCase()}${formattedStartDate.slice(1)}.`;
    } else if (order.type === OrderType.Continuous) {
      // Continuous orders
      return `${formattedStart}. -`;
    }

    // Bundle and fixed term orders
    return `${formattedStart}. - ${formattedEnd}.`;
  };

  const renderDateColumValues = (
    order: OrderRowDisplay,
    orderDateObject: Date,
  ) => (
    <Box display="flex" flexWrap="wrap">
      <Typography width="80px" fontSize="14px">
        {formatDate(order, orderDateObject)}
      </Typography>
      <span>{getStatusIcons(order)}</span>
    </Box>
  );

  const renderTime = useCallback(
    (order: OrderRowDisplay) => {
      if (!order.isFlexibleShift || order.shifts.length !== 1)
        return (
          <Typography fontSize="14px" pl="18px">
            {formatTime(order)}
          </Typography>
        );

      return (
        <Box display="flex" alignItems="center" gap="4px">
          <Tooltip title={formatFlexibleStartTime(order.shifts[0], t)}>
            <ClockIcon width="14px" height="14px" style={{ flexShrink: 0 }} />
          </Tooltip>
          <Typography fontSize="14px">{formatTime(order)}</Typography>
        </Box>
      );
    },
    [t],
  );

  const getRowVariant = (order: OrderRowDisplay, now: Date) => {
    if (!canReadOrder(order.eezyCompanyId)) {
      return "disabled" as const;
    }
    if (order.status === ORDER_STATUS_EXPIRED) {
      return "secondary" as const;
    }
    // Check manually if short order is expired (active but has started)
    if (order.type === OrderType.Short && order.status === "Active") {
      const orderStart = order.shifts[0].flexibleStartUntil || order.workStarts;
      if (new Date(orderStart) <= now) {
        return "secondary" as const;
      }
    }
    return undefined;
  };

  const formatOrderRow = useCallback(
    (order: OrderRowDisplay, orderDateObject: Date) => {
      const now = new Date();
      const url = `/home/orders/${order.bundleId}`;
      return {
        ...order,
        url,
        rowVariant: getRowVariant(order, now),
        date: renderDateColumValues(order, orderDateObject),
        time: renderTime(order),
        fillShiftText: order.isFillShift ? t("fillShift") : "",
        fulfilledState: (
          <OrderFulfillmentInfo
            amountFulfilled={order.amountFulfilled}
            amountOrdered={order.amountOrdered}
            originalAmountOrdered={order.originalAmountOrdered}
          />
        ),
        workTitle: localized.workTitle[order.workTitleId],
        orderHandlers: !isEmpty(order.orderHandlers) ? (
          <>
            {order.orderHandlers.map((handler) => (
              <Typography key={handler.id} fontSize={14}>
                {handler.name}
              </Typography>
            ))}
          </>
        ) : (
          t("order:noHandler")!!
        ),
        assignedTalents: getAssignedTalents(
          order.assignedTalents,
          t("showTalents"),
        ),
        more: (
          <HoverPopper
            placement="top-start"
            trigger={
              <IconButton
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            }
          >
            <OrderRowPopperContent
              title={order.title}
              workStarts={order.workStarts}
              workEnds={order?.workEnds}
              taskSpec={order.taskSpec}
              type={order.type}
              onClick={() => navigate(url)}
            />
          </HoverPopper>
        ),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localized.workTitle, t],
  );

  // Grouping by order date (similar code in invoices)
  // share code?
  const constructOrderGroups = useCallback(
    (orders: OrderRowDisplay[]) => {
      let groups: OrderGroup[] = [];
      orders.forEach((order) => {
        const orderStartsDate = !!order.workStarts
          ? new Date(order.workStarts)
          : new Date();
        const orderYear = orderStartsDate.getFullYear();
        let groupName: string;

        if (order.isFillShift) {
          groupName = "fillShift";
        } else if (isToday(orderStartsDate)) {
          // Today
          groupName = "today";
        } else if (isThisISOWeek(orderStartsDate)) {
          // This calendar week
          groupName = "thisWeek";
        } else {
          // Current and/or previous months
          groupName = `${orderYear}-${String(orderStartsDate.getMonth() + 1).padStart(2, "0")}`;
        }

        const formattedOrder = formatOrderRow(order, orderStartsDate);
        //use reduce?
        const orderGroup = groups.find((groups) => groups.name === groupName);
        if (!orderGroup) {
          groups.push({
            name: groupName,
            year:
              orderYear < new Date().getFullYear()
                ? orderYear.toString()
                : undefined,
            items: [formattedOrder],
          });
        } else {
          orderGroup.items.push(formattedOrder);
        }
      });
      const now = new Date();
      const todayStart = now.setHours(0, 0, 0, 0);
      const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const getGroupSortKey = (name: string): number => {
        if (name === "fillShift") return -Infinity;
        if (name === "today") return todayStart;
        if (name === "thisWeek") return todayStart + 1;
        if (name === currentYearMonth) return todayStart + 2;
        // Past/future YYYY-MM groups: use the first day of that month as timestamp
        const [year, month] = name.split("-").map(Number);
        return new Date(year, month - 1, 1).getTime();
      };
      groups.sort((a, b) => getGroupSortKey(a.name) - getGroupSortKey(b.name));
      setOrderGroups(groups);
    },
    [formatOrderRow],
  );

  useEffect(() => {
    constructOrderGroups(pageData);
  }, [pageData, constructOrderGroups]);

  useEffect(() => {
    resetPagination();
    dispatchSearch();
  }, [filters, searchString, dispatchSearch, resetPagination]);

  return (
    <Box data-testid="orders-page-root">
      <Heading1>{t("common:orders")}</Heading1>
      <OrderQuickFilterButtons
        selectedQuickFilter={selectedQuickFilter}
        onQuickFilterChange={handleQuickFilterChange}
      />
      <FilterSearchField
        isOpen={isFilterMenuOpen}
        toggleMenu={handleFilterMenuOpenClose}
        label={t("searchOrder") || ""}
        value={searchString}
        onChange={(value) => setSearchString(value)}
        searchHistory={true}
        selectFromHistory={selectPrevTerm}
        translation={"order"}
        filters={filters}
        updateFilters={updateFilters}
        isLoading={isLoading}
        localStorageName="searchOrder"
        filtersLocalStorageName={ORDER_FILTERS_LOCAL_STORAGE}
        defaultFilters={defaultOrderFilters}
        width="1100px"
      >
        <OrderFilters
          filters={filters}
          updateFilters={updateFilters}
          handleMenuToggle={handleFilterMenuOpenClose}
        />
      </FilterSearchField>
      <OrdersTable
        isLoading={isLoading}
        orderGroups={orderGroups}
        paginationProps={paginationProps}
        itemCount={count.value}
      />
    </Box>
  );
};
